import React,{useEffect,useMemo,useState}from"react";
import{toast}from"react-toastify";
import * as XLSX from"xlsx";
import axiosInstance from"../axiosInstance";
import"./BulkImportsWorkspace.css";

const ecosureFields=[
 ["name","Member name"],["national_id","National ID"],["date_of_birth","Date of birth"],
 ["gender","Gender"],["premium","EcoSure premium (USD)"],["benefit","Benefit"],
 ["date_joined","Date joined"],["notes","Notes"],
];

const paymentFields=[
 ["policy_number","Policy number"],["name","Payer / member name"],
 ["payment_date","Payment date"],["reference","Receipt / reference"],
 ["zar_amount","ZAR amount"],["usd_amount","USD amount"],
 ["month_paid_for","Month paid for"],["notes","Notes"],
];

const ecosureGuesses={
 name:["NAME & SURNAME","NAME AND SURNAME","MEMBER NAME","NAME"],
 national_id:["ID NUMBERS","ID NUMBER","NATIONAL ID","ID NO","ID"],
 date_of_birth:["DATE OF BIRTH","DOB","D.O.B"],gender:["GENDER","GENGER","SEX"],
 premium:["PREMIUM","ECOSURE PREMIUM","DEDUCTION"],benefit:["BENEFITS","BENEFIT"],
 date_joined:["D.O.J","DOJ","DATE JOINED","JOINING DATE"],notes:["NOTES","NOTE","COMMENTS"],
};

const paymentGuesses={
 policy_number:["POLICY NO.","POLICY NO","POLICY NUMBER","POLICY","POLICY #"],
 name:["NAME & SURNAME","NAME AND SURNAME","NAME","MEMBER NAME","CLIENT"],
 payment_date:["DATE","DATES","PAYMENT DATE","DATE OF PAYMENT","RECEIPT DATE"],
 reference:["RECEIPT","RECEIPTS","RECEIPT NO","RECEIPT NUMBER","REFERENCE","REF","RECEIPT #"],
 zar_amount:["ZAR","ZAR AMOUNT","RAND","RANDS","RAND AMOUNT","ZAR $","CASH ZAR"],
 usd_amount:["USD $","USD","USD AMOUNT","US$","US $","USD CASH"],
 month_paid_for:["MONTH PAID FOR","MONTH","PERIOD PAID FOR","MONTHS PAID FOR"],
 notes:["NOTES","NOTE","COMMENTS","REMARKS"],
};

const norm=v=>String(v??"").trim().toUpperCase().replace(/\s+/g," ");

function autoMap(headers,mode){
 const fields=mode==="PAYMENTS"?paymentFields:ecosureFields;
 const guesses=mode==="PAYMENTS"?paymentGuesses:ecosureGuesses;
 const result={};
 fields.forEach(([key])=>{
  const wanted=guesses[key]||[];
  result[key]=headers.find(h=>wanted.includes(norm(h)))||"";
 });
 return result;
}

function money(v){return v===null||v===undefined||v===""?"—":v;}

export default function BulkImportsWorkspace(){
 const[mode,setMode]=useState("ECOSURE");
 const[fileName,setFileName]=useState("");
 const[workbook,setWorkbook]=useState(null);
 const[sheetNames,setSheetNames]=useState([]);
 const[sheetName,setSheetName]=useState("");
 const[rows,setRows]=useState([]);
 const[headers,setHeaders]=useState([]);
 const[mapping,setMapping]=useState({});
 const[preview,setPreview]=useState(null);
 const[busy,setBusy]=useState(false);
 const[filter,setFilter]=useState("");
 const[selected,setSelected]=useState(new Set());
 const[history,setHistory]=useState([]);
 const[historyDetail,setHistoryDetail]=useState(null);
 const[historyBusy,setHistoryBusy]=useState(false);
 const[zarMethods,setZarMethods]=useState([]);
 const[usdMethods,setUsdMethods]=useState([]);
 const[zarPaymentMethod,setZarPaymentMethod]=useState("");
 const[usdPaymentMethod,setUsdPaymentMethod]=useState("");
 const[systemOnlySelected,setSystemOnlySelected]=useState(new Set());

 const fields=mode==="PAYMENTS"?paymentFields:ecosureFields;

 const loadHistory=async(currentMode=mode)=>{
  try{
   const url=currentMode==="PAYMENTS"?"bulk-imports/payments/history/":"bulk-imports/ecosure/history/";
   const r=await axiosInstance.get(url);
   setHistory(r.data?.results||[]);
  }catch(e){setHistory([]);}
 };

 const loadPaymentOptions=async()=>{
  try{
   const r=await axiosInstance.get("bulk-imports/payments/options/");
   const z=r.data?.zar_payment_methods||[];
   const u=r.data?.usd_payment_methods||[];
   setZarMethods(z);setUsdMethods(u);
   if(!zarPaymentMethod&&z.length===1)setZarPaymentMethod(String(z[0].id));
   if(!usdPaymentMethod&&u.length===1)setUsdPaymentMethod(String(u[0].id));
  }catch(e){toast.error("Could not load ZAR/USD payment methods.");}
 };

 useEffect(()=>{loadHistory(mode);if(mode==="PAYMENTS")loadPaymentOptions();},[mode]);

 const loadSheet=(wb,name,currentMode=mode)=>{
  const json=XLSX.utils.sheet_to_json(wb.Sheets[name],{defval:"",raw:false});
  const found=[...new Set(json.flatMap(r=>Object.keys(r)))];
  setSheetName(name);setRows(json);setHeaders(found);
  setMapping(autoMap(found,currentMode));
  setPreview(null);setSelected(new Set());setFilter("");
 };

 const readFile=async e=>{
  const file=e.target.files?.[0];if(!file)return;
  setBusy(true);setPreview(null);setSelected(new Set());
  try{
   const buffer=await file.arrayBuffer();
   const wb=XLSX.read(buffer,{type:"array",cellDates:true});
   const first=wb.SheetNames[0];
   setFileName(file.name);setWorkbook(wb);setSheetNames(wb.SheetNames);
   loadSheet(wb,first,mode);
   toast.success(`Loaded ${file.name}. The original WPS/Excel file has not been changed.`);
  }catch(err){toast.error("Could not read this WPS/Excel workbook.");}
  finally{setBusy(false);}
 };

 const changeSheet=name=>{if(workbook)loadSheet(workbook,name,mode);};

 const switchMode=next=>{
  setMode(next);setPreview(null);setSelected(new Set());setFilter("");setHistoryDetail(null);
  if(workbook&&sheetName)loadSheet(workbook,sheetName,next);
 };

 const runPreview=async()=>{
  if(!rows.length)return toast.error("Choose an Excel worksheet first.");
  setBusy(true);
  try{
   const url=mode==="PAYMENTS"?"bulk-imports/payments/preview/":"bulk-imports/ecosure/preview/";
   const payload={rows,mapping};
   if(mode==="PAYMENTS"){
    payload.zar_payment_method=zarPaymentMethod||null;
    payload.usd_payment_method=usdPaymentMethod||null;
   }
   const r=await axiosInstance.post(url,payload);
   setPreview(r.data);
   setSelected(new Set((r.data?.results||[]).filter(x=>x.safe_to_apply).map(x=>x.excel_row)));
   setSystemOnlySelected(new Set());
   toast.success(`${mode==="PAYMENTS"?"Payment":"EcoSure"} preview completed. Nothing has been posted yet.`);
  }catch(e){toast.error(e?.response?.data?.detail||"Could not run reconciliation preview.");}
  finally{setBusy(false);}
 };

 const toggleRow=row=>setSelected(prev=>{const next=new Set(prev);next.has(row)?next.delete(row):next.add(row);return next;});
 const selectSafe=()=>setSelected(new Set((preview?.results||[]).filter(x=>x.safe_to_apply).map(x=>x.excel_row)));
 const clearSelected=()=>setSelected(new Set());

 const applyRows=async(approvedRows,confirmMessage)=>{
  if(!approvedRows.length)return toast.error("There are no validated rows to apply.");
  const label=mode==="PAYMENTS"?"payment":"EcoSure";
  if(!window.confirm(confirmMessage||`Apply ${approvedRows.length} selected ${label} row(s)? The server will revalidate everything first.`))return;
  setBusy(true);
  try{
   const url=mode==="PAYMENTS"?"bulk-imports/payments/apply/":"bulk-imports/ecosure/apply/";
   const payload={rows,mapping,approved_rows:approvedRows,file_name:fileName,sheet_name:sheetName};
   if(mode==="PAYMENTS"){
    payload.zar_payment_method=zarPaymentMethod||null;
    payload.usd_payment_method=usdPaymentMethod||null;
   }
   const r=await axiosInstance.post(url,payload);
   toast.success(`${mode==="PAYMENTS"?"Payment":"EcoSure"} batch ${r.data.batch_id} applied successfully.`);
   await runPreview();await loadHistory(mode);
  }catch(e){toast.error(e?.response?.data?.detail||"Batch could not be applied. No partial changes were kept.");}
  finally{setBusy(false);}
 };

 const applySelected=()=>applyRows([...selected]);

 const applyAllValidated=()=>{
  if(mode!=="ECOSURE")return;
  const results=preview?.results||[];
  const safeRows=results.filter(x=>x.safe_to_apply).map(x=>x.excel_row);
  const excluded=results.length-safeRows.length;
  if(!safeRows.length)return toast.error("There are no validated EcoSure rows to apply.");
  applyRows(
   safeRows,
   `Apply all ${safeRows.length} validated EcoSure row(s)?\n\n${excluded} row(s) requiring review will NOT be applied.\n\nThe server will revalidate all selected rows against the live database before applying anything.`
  );
 };

 const safeFilePart=v=>String(v||"export").replace(/[^A-Za-z0-9_-]+/g,"_").replace(/^_+|_+$/g,"")||"export";

 const downloadWorkbook=(data,fileBase,sheetLabel="Results")=>{
  if(!data.length)return toast.error("There are no rows to export.");
  const ws=XLSX.utils.json_to_sheet(data);
  const wb=XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb,ws,sheetLabel.slice(0,31));
  XLSX.writeFile(wb,`${safeFilePart(fileBase)}.xlsx`);
 };

 const toggleSystemOnly=id=>setSystemOnlySelected(prev=>{const next=new Set(prev);next.has(id)?next.delete(id):next.add(id);return next;});
 const selectAllSystemOnly=()=>setSystemOnlySelected(new Set((preview?.system_only||[]).map(x=>x.id)));
 const clearSystemOnly=()=>setSystemOnlySelected(new Set());

 const deregisterSystemOnly=async()=>{
  const ids=[...systemOnlySelected];
  if(!ids.length)return toast.error("Select at least one system-only EcoSure member.");
  const reason=window.prompt(`Reason for deregistering ${ids.length} system-only EcoSure member(s):`,"NOT FOUND IN VERIFIED ECOSURE REGISTER");
  if(reason===null)return;
  if(String(reason).trim().length<5)return toast.error("Enter a clear deregistration reason.");
  if(!window.confirm(`Deregister ${ids.length} selected EcoSure member(s)?

This clears their current EcoSure flag and deduction only.
EcoSure D.O.J and protected financial history are preserved.

The server will first re-run the uploaded-register reconciliation.`))return;
  setBusy(true);
  try{
   const r=await axiosInstance.post("bulk-imports/ecosure/system-only/deregister/",{
    rows,mapping,approved_member_ids:ids,reason:String(reason).trim(),file_name:fileName,sheet_name:sheetName
   });
   toast.success(`EcoSure batch ${r.data.batch_id}: ${r.data.changed||0} member(s) deregistered.`);
   await runPreview();await loadHistory("ECOSURE");
  }catch(e){toast.error(e?.response?.data?.detail||"EcoSure deregistration failed. No partial changes were kept.");}
  finally{setBusy(false);}
 };

 const exportSystemOnly=()=>{
  const rows=preview?.system_only||[];
  if(!rows.length)return toast.info("There are no NetriAssure-only EcoSure members in this preview.");

  const data=rows.map(x=>({
   "Member Code":x.code||"",
   "Member Name":x.name||"",
   "Policy Number":x.policy_number||"",
   "National ID":x.national_id||"",
   "Date of Birth":x.date_of_birth||"",
   "Member Status":x.member_status||x.status||"",
   "Policy Status":x.policy_status||"",
   "Current Tier USD":x.ecosure_enabled?x.ecosure_deduction||"0.00":"OFF",
   "EcoSure D.O.J":x.ecosure_effective_date||"",
   "Reason":x.reason||"NOT FOUND IN UPLOADED ECOSURE REGISTER",
  }));

  downloadWorkbook(
   data,
   `ECOSURE_SYSTEM_ONLY_${fileName||sheetName||"preview"}`,
   "System Only"
  );
 };

 const exportExceptions=()=>{
  const rows=(preview?.results||[]).filter(x=>!x.safe_to_apply);
  if(!rows.length)return toast.info("There are no unmatched/review rows in this preview.");

  const data=rows.map(x=>mode==="PAYMENTS"?{
   "Excel Row":x.excel_row,
   "Name":x.excel?.name||"",
   "Policy Value":x.excel?.policy_number||"",
   "Date":x.excel?.payment_date||"",
   "Receipt / Reference":x.excel?.reference||"",
   "ZAR Amount":x.excel?.zar_amount||"",
   "USD Amount":x.excel?.usd_amount||"",
   "Status":x.status,
   "Matched Policy":x.policy?.code||"",
   "Match Basis":x.match_basis||"",
   "Reason":(x.issues||[]).join(" | "),
  }:{
   "Excel Row":x.excel_row,
   "Name":x.excel?.name||"",
   "National ID":x.excel?.national_id||"",
   "Date of Birth":x.excel?.date_of_birth||"",
   "EcoSure Premium USD":x.excel?.premium_usd||"",
   "Notes":x.excel?.notes||"",
   "Status":x.status,
   "Matched Member":x.member?.name||"",
   "Matched Policy":x.member?.policy_number||"",
   "Match Basis":x.match_basis||"",
   "Proposed Action":x.proposed_action||"",
   "Reason":(x.issues||[]).join(" | "),
  });

  downloadWorkbook(
   data,
   `${mode==="PAYMENTS"?"PAYMENT":"ECOSURE"}_IMPORT_EXCEPTIONS_${fileName||sheetName||"preview"}`,
   "Exceptions"
  );
 };

 const openHistory=async batch=>{
  setHistoryBusy(true);
  try{
   const url=mode==="PAYMENTS"?"bulk-imports/payments/history/":"bulk-imports/ecosure/history/";
   const r=await axiosInstance.get(url,{params:{batch_id:batch.batch_id}});
   setHistoryDetail(r.data);
  }catch(e){
   toast.error(e?.response?.data?.detail||"Could not load import batch details.");
  }finally{setHistoryBusy(false);}
 };

 const exportHistorySuccess=()=>{
  const rows=historyDetail?.rows||[];
  if(!rows.length)return toast.info("This batch has no successful row audit records.");

  const data=rows.map(x=>mode==="PAYMENTS"?{
   "Excel Row":x.excel_row||"",
   "Receipt Code":x.receipt_code||x.entity_id||"",
   "Policy Number":x.policy_number||"",
   "External Reference":x.external_reference||"",
   "Payment Date":x.payment_date||"",
   "Payment Method":x.payment_method||"",
   "Source Amount":x.source_amount||"",
   "Source Currency":x.source_currency||"",
   "Exchange Rate":x.effective_rate||"",
   "Policy Amount":x.home_amount||"",
   "Policy Currency":x.home_currency||"",
   "Allocated":x.allocated_amount||"",
   "Credit":x.credit_amount||"",
   "Month Paid For":x.month_paid_for||"",
  }:{
   "Excel Row":x.excel_row||"",
   "Member Code":x.member_code||x.entity_id||"",
   "Member ID":x.member_id||"",
   "Policy Number":x.policy_number||"",
   "Match Basis":x.match_basis||"",
   "Action":x.action||"",
   "Before Enabled":x.before?.ecosure_enabled??"",
   "Before Tier USD":x.before?.ecosure_deduction||"",
   "After Enabled":x.after?.ecosure_enabled??"",
   "After Tier USD":x.after?.ecosure_deduction||"",
  });

  downloadWorkbook(
   data,
   `${mode==="PAYMENTS"?"PAYMENT":"ECOSURE"}_SUCCESS_${historyDetail?.batch?.batch_id||"batch"}`,
   "Successful"
  );
 };

 const visible=useMemo(()=>{const all=preview?.results||[];return filter?all.filter(x=>x.status===filter):all;},[preview,filter]);
 const s=preview?.summary||{};
 const paymentStatuses=["MATCHED","NOT FOUND","DUPLICATE EXCEL","ALREADY IMPORTED","MULTI CURRENCY","AMBIGUOUS","AMBIGUOUS NAME","INVALID","NO OUTSTANDING BILLS"];
 const ecosureStatuses=["MATCHED","POSSIBLE MATCH","NOT FOUND","DUPLICATE EXCEL","AMBIGUOUS","INVALID"];
 const statuses=mode==="PAYMENTS"?paymentStatuses:ecosureStatuses;

 return <section className="bulk-page">
  <header className="bulk-hero"><div><span>CONTROLLED DATA INTAKE</span><h1>Bulk Imports & Reconciliation</h1><p>Upload the client's original WPS/Excel file, validate it in NetriAssure, and post only approved records.</p></div><div className="bulk-mode"><button className={mode==="ECOSURE"?"active":""} onClick={()=>switchMode("ECOSURE")}>EcoSure</button><button className={mode==="PAYMENTS"?"active":""} onClick={()=>switchMode("PAYMENTS")}>Payments</button></div></header>

  <div className="bulk-warning"><i className="bi bi-shield-check"/><div><strong>{mode==="PAYMENTS"?"ZAR / USD financial safety mode":"Controlled apply mode"}</strong><span>{mode==="PAYMENTS"?"Each row keeps its original tender currency. NetriAssure converts only when the tender currency differs from the matched policy currency. The preview shows the live conversion result before posting.":"Only exact server-revalidated matches can be applied automatically."}</span></div></div>

  <div className="bulk-grid">
   <section className="bulk-card upload-card"><header><span>STEP 1</span><h2>Choose WPS / Excel workbook</h2></header><label className="bulk-drop"><i className="bi bi-file-earmark-excel"/><strong>{fileName||"Select .xls or .xlsx file"}</strong><span>{sheetName?`Sheet: ${sheetName} · ${rows.length} row(s)`:"The file is read in the browser; it is not resaved or modified."}</span><input type="file" accept=".xls,.xlsx" onChange={readFile}/></label>{sheetNames.length>1?<label className="sheet-select"><span>Worksheet</span><select value={sheetName} onChange={e=>changeSheet(e.target.value)}>{sheetNames.map(n=><option key={n} value={n}>{n}</option>)}</select></label>:null}</section>

   <section className="bulk-card"><header><span>STEP 2</span><h2>Map Excel columns</h2></header><div className="mapping-grid">{fields.map(([key,label])=><label key={key}><span>{label}</span><select value={mapping[key]||""} onChange={e=>setMapping({...mapping,[key]:e.target.value})}><option value="">Not mapped</option>{headers.map(h=><option key={h} value={h}>{h}</option>)}</select></label>)}</div>
    {mode==="PAYMENTS"?<div className="currency-methods">
      <label><span>ZAR payment method</span><select value={zarPaymentMethod} onChange={e=>setZarPaymentMethod(e.target.value)}><option value="">Choose ZAR method</option>{zarMethods.map(m=><option key={m.id} value={m.id}>{m.name} · ZAR</option>)}</select><small>ZAR rows use this tender. No foreign-currency conversion is required when ZAR is the policy/base currency.</small></label>
      <label><span>USD payment method</span><select value={usdPaymentMethod} onChange={e=>setUsdPaymentMethod(e.target.value)}><option value="">Choose USD method</option>{usdMethods.map(m=><option key={m.id} value={m.id}>{m.name} · USD</option>)}</select><small>USD rows preserve the USD source amount and use NetriAssure's configured currency conversion before allocation.</small></label>
    </div>:null}
    <footer><button className="primary" disabled={busy||!rows.length} onClick={runPreview}>{busy?"Working...":"Validate & preview"}</button></footer>
   </section>
  </div>

  {preview&&<>
   <div className="bulk-summary"><article><span>Processed</span><strong>{preview.rows_processed}</strong></article><article className="good"><span>Policies matched</span><strong>{s.matched||0}</strong></article><article className="good"><span>Safe to apply</span><strong>{s.safe_to_apply||0}</strong></article>{mode==="PAYMENTS"?<><article className="currency-zar"><span>ZAR rows</span><strong>{s.zar_rows||0}</strong></article><article className="currency-usd"><span>USD rows</span><strong>{s.usd_rows||0}</strong></article><article><span>Both currencies</span><strong>{s.multi_currency||0}</strong></article><article><span>Already imported</span><strong>{s.already_imported||0}</strong></article><article className="good"><span>Advance credit</span><strong>{s.advance_credit||0}</strong></article><article><span>Invalid</span><strong>{s.invalid||0}</strong></article></>:<><article><span>Possible</span><strong>{s.possible_match||0}</strong></article><article><span>Not found</span><strong>{s.not_found||0}</strong></article><article><span>System only</span><strong>{s.system_only||0}</strong></article><article><span>Duplicates</span><strong>{s.duplicate_excel||0}</strong></article><article><span>Invalid</span><strong>{s.invalid||0}</strong></article></>}</div>


   <section className="bulk-results bulk-card"><header><div><span>STEP 3</span><h2>Reconciliation preview</h2></div><div className="bulk-result-tools"><button onClick={exportExceptions}><i className="bi bi-file-earmark-excel"/> Export unmatched / review</button><select value={filter} onChange={e=>setFilter(e.target.value)}><option value="">All results</option>{statuses.map(x=><option key={x}>{x}</option>)}</select></div></header><div className="bulk-table-wrap"><table>
    {mode==="PAYMENTS"?<><thead><tr><th className="select-col">Post</th><th>Excel row</th><th>Payment</th><th>Policy match</th><th>Currency treatment</th><th>Allocation</th><th>Result</th></tr></thead><tbody>{visible.map(x=><tr key={x.excel_row} className={selected.has(x.excel_row)?"selected-row":""}>
      <td className="select-col"><input type="checkbox" checked={selected.has(x.excel_row)} disabled={!x.safe_to_apply||busy} onChange={()=>toggleRow(x.excel_row)}/></td>
      <td>{x.excel_row}</td>
      <td><strong>{x.excel.policy_number||"—"} · REF {x.excel.reference||"—"}</strong><small>{x.excel.payment_date||"NO DATE"} · {x.excel.name||""}</small><small>{x.excel.month_paid_for?`Month: ${x.excel.month_paid_for}`:""}</small></td>
      <td>{x.policy?<><strong>{x.policy.code}</strong><small>{x.policy.policyholder} · {x.policy.status}</small><small className="match-basis">MATCHED BY {x.match_basis}</small><small>Balance: {x.policy.currency_code} {x.policy.balance}</small></>:<><strong>—</strong><small>No safe policy match</small></>}</td>
      <td>{x.source_currency?<><strong className={x.source_currency==="USD"?"usd-text":"zar-text"}>{x.source_currency} {money(x.source_amount)}</strong><small>{x.policy?(x.source_currency===x.policy.currency_code?`No FX conversion · ${x.source_currency} → ${x.policy.currency_code}`:`FX conversion · ${x.source_currency} → ${x.policy.currency_code}`):`System base: ${x.system_base_currency||preview.system_base_currency||"—"}`}</small><small>{x.converted_home_amount?`Policy amount: ${x.policy?.currency_code||""} ${x.converted_home_amount}`:""}</small><small>{x.conversion_rate?`Configured source rate: ${x.conversion_rate}`:""}</small></>:<><strong>—</strong><small>Currency needs review</small></>}</td>
      <td>{x.bill_plan?<><strong>{x.policy?.currency_code} {x.bill_plan.allocated_amount}</strong><small>Outstanding before: {x.bill_plan.outstanding_before}</small><small>Credit: {x.bill_plan.credit_amount}</small></>:<span>—</span>}</td>
      <td><span className={`result-pill ${x.status.toLowerCase().replaceAll(" ","-")}`}>{x.status}</span>{x.issues?.map((i,n)=><small key={n}>{i}</small>)}</td>
    </tr>)}</tbody></>:<><thead><tr><th className="select-col">Apply</th><th>Excel row</th><th>Excel member</th><th>NetriAssure match</th><th>EcoSure</th><th>Result</th><th>Proposed action</th></tr></thead><tbody>{visible.map(x=><tr key={x.excel_row} className={selected.has(x.excel_row)?"selected-row":""}><td className="select-col"><input type="checkbox" checked={selected.has(x.excel_row)} disabled={!x.safe_to_apply||busy} onChange={()=>toggleRow(x.excel_row)}/></td><td>{x.excel_row}</td><td><strong>{x.excel.name||"—"}</strong><small>{x.excel.national_id||"NO ID"} · {x.excel.date_of_birth||"NO DOB"}</small></td><td>{x.member?<><strong>{x.member.name}</strong><small>{x.member.policy_number} · {x.match_basis}</small></>:<strong>—</strong>}</td><td><strong>{x.excel.premium_usd?`USD ${x.excel.premium_usd}`:"—"}</strong><small>{x.member?`Current: ${x.member.ecosure_enabled?`USD ${x.member.ecosure_deduction}`:"OFF"}`:""}</small></td><td><span className={`result-pill ${x.status.toLowerCase().replaceAll(" ","-")}`}>{x.status}</span>{x.issues?.map((i,n)=><small key={n}>{i}</small>)}</td><td><strong>{x.proposed_action}</strong><small>{x.excel.notes||""}</small></td></tr>)}</tbody></>}
   </table></div><footer><span><b>Nothing changes until Post/Apply is confirmed.</b> The server re-runs validation using the live database and current configured currency rates immediately before posting.</span></footer></section>

   {mode==="ECOSURE"&&<section className="bulk-system-only bulk-card">
    <header><div><span>SYSTEM CROSS-CHECK</span><h2>NetriAssure EcoSure members missing from uploaded register</h2><p>These are currently marked EcoSure in NetriAssure but are not represented by a resolved identity in this uploaded register. Deregistration is System Administrator only and is revalidated by the server.</p></div><div className="system-only-actions"><button onClick={exportSystemOnly} disabled={!preview?.system_only?.length}><i className="bi bi-file-earmark-excel"/> Export system only</button><button onClick={selectAllSystemOnly} disabled={busy||!preview?.system_only?.length}>Select all</button><button onClick={clearSystemOnly} disabled={busy||!systemOnlySelected.size}>Clear</button><button className="danger" onClick={deregisterSystemOnly} disabled={busy||!systemOnlySelected.size}>{busy?"Working...":`Deregister selected (${systemOnlySelected.size})`}</button></div></header>
    <div className="bulk-table-wrap"><table>
     <thead><tr><th className="select-col">Remove</th><th>Member</th><th>Policy</th><th>National ID / DOB</th><th>Current EcoSure</th><th>Status</th><th>Review reason</th></tr></thead>
     <tbody>{(preview?.system_only||[]).map(x=><tr key={x.id} className={systemOnlySelected.has(x.id)?"selected-row":""}>
      <td className="select-col"><input type="checkbox" checked={systemOnlySelected.has(x.id)} disabled={busy} onChange={()=>toggleSystemOnly(x.id)}/></td>
      <td><strong>{x.name||"—"}</strong><small>{x.code||""}</small></td>
      <td><strong>{x.policy_number||"—"}</strong><small>{x.policy_status||""}</small></td>
      <td><strong>{x.national_id||"NO ID"}</strong><small>{x.date_of_birth||"NO DOB"}</small></td>
      <td><strong>{x.ecosure_enabled?`USD ${x.ecosure_deduction||"0.00"}`:"OFF"}</strong><small>D.O.J: {x.ecosure_effective_date||"NOT RECORDED"}</small></td>
      <td><span className="result-pill system-only">{x.member_status||x.status||"—"}</span></td>
      <td><strong>{x.reason||"NOT FOUND IN UPLOADED ECOSURE REGISTER"}</strong><small>Deregistration preserves EcoSure D.O.J and protected financial history.</small></td>
     </tr>)}{!preview?.system_only?.length&&<tr><td colSpan="7">No NetriAssure-only EcoSure members were found for this upload.</td></tr>}</tbody>
    </table></div>
    <footer><span><b>Controlled removal:</b> selected members are checked again against the uploaded register immediately before any change. Current EcoSure is disabled and deduction set to zero; historical D.O.J and protected financial transactions remain.</span></footer>
   </section>}

   <section className="bulk-apply-bar bulk-card"><div><span>STEP 4</span><h2>{mode==="PAYMENTS"?"Post approved payments":"Apply approved EcoSure changes"}</h2><p><strong>{selected.size}</strong> safe row(s) selected. {mode==="PAYMENTS"?"The original payment date and source currency are preserved in the receipt/audit trail.":"Every apply creates a permanent audit batch."}</p></div><div className="bulk-apply-actions"><button onClick={selectSafe} disabled={busy}>Select all safe</button><button onClick={clearSelected} disabled={busy||!selected.size}>Clear</button>{mode==="ECOSURE"&&<button className="primary" onClick={applyAllValidated} disabled={busy||!(s.safe_to_apply||0)}>{busy?"Working...":`Apply all ${s.safe_to_apply||0} validated`}</button>}<button className={mode==="PAYMENTS"?"primary":""} onClick={applySelected} disabled={busy||!selected.size}>{busy?"Working...":`${mode==="PAYMENTS"?"Post":"Apply"} ${selected.size} selected`}</button></div></section>
  </>}

  <section className="bulk-history bulk-card">
   <header><div><span>IMPORT HISTORY</span><h2>Recent {mode==="PAYMENTS"?"payment":"EcoSure"} batches</h2></div><button onClick={()=>loadHistory(mode)}>Refresh</button></header>
   {history.length?<div className="history-list">{history.map(x=><article key={x.batch_id} className="history-clickable" onClick={()=>openHistory(x)}>
    <div><strong>{x.batch_id}</strong><small>{x.file_name||"Excel import"}{x.sheet_name?` · ${x.sheet_name}`:""}</small></div>
    {mode==="PAYMENTS"?<div><span>{x.posted||0} posted</span><span>ZAR {x.total_zar_source||"0.00"}</span><span>USD {x.total_usd_source||"0.00"}</span><span>Home {x.total_home_amount||"0.00"}</span></div>:<div><span>{x.changed||0} changed</span><span>{x.enabled||0} enabled</span><span>{x.tier_updated||0} tier updates</span><span>{x.removed||0} removed</span></div>}
    <time>{x.created_at?new Date(x.created_at).toLocaleString():""}</time>
   </article>)}</div>:<div className="history-empty">No {mode==="PAYMENTS"?"payment":"EcoSure"} Excel batches have been applied yet.</div>}

   {historyBusy?<div className="history-empty">Loading batch details...</div>:null}

   {historyDetail?<div className="history-detail">
    <div className="history-detail-head">
     <div><span>BATCH DETAIL</span><h3>{historyDetail.batch?.batch_id}</h3><small>{historyDetail.batch?.file_name||"Excel import"} · {historyDetail.rows?.length||0} successful row(s)</small></div>
     <div><button onClick={exportHistorySuccess}><i className="bi bi-file-earmark-excel"/> Export successful</button><button onClick={()=>setHistoryDetail(null)}>Close</button></div>
    </div>
    <div className="history-detail-table"><table>
     {mode==="PAYMENTS"?<><thead><tr><th>Excel row</th><th>Receipt</th><th>Policy</th><th>Reference</th><th>Date</th><th>Source</th><th>Policy amount</th><th>Allocated</th><th>Credit</th></tr></thead><tbody>{(historyDetail.rows||[]).map((r,i)=><tr key={`${r.entity_id}-${i}`}><td>{r.excel_row||"—"}</td><td>{r.receipt_code||r.entity_id||"—"}</td><td>{r.policy_number||"—"}</td><td>{r.external_reference||"—"}</td><td>{r.payment_date||"—"}</td><td>{r.source_currency||""} {r.source_amount||""}</td><td>{r.home_currency||""} {r.home_amount||""}</td><td>{r.allocated_amount||"0.00"}</td><td>{r.credit_amount||"0.00"}</td></tr>)}</tbody></>:<><thead><tr><th>Excel row</th><th>Member</th><th>Policy</th><th>Match</th><th>Action</th><th>Before</th><th>After</th></tr></thead><tbody>{(historyDetail.rows||[]).map((r,i)=><tr key={`${r.entity_id}-${i}`}><td>{r.excel_row||"—"}</td><td>{r.member_code||r.entity_id||"—"}</td><td>{r.policy_number||"—"}</td><td>{r.match_basis||"—"}</td><td>{r.action||"—"}</td><td>{r.before?.ecosure_enabled?`ON · USD ${r.before?.ecosure_deduction||"0.00"}`:"OFF"}</td><td>{r.after?.ecosure_enabled?`ON · USD ${r.after?.ecosure_deduction||"0.00"}`:"OFF"}</td></tr>)}</tbody></>}
    </table></div>
   </div>:null}
  </section>
 </section>;
}
